import React, { useEffect, useState } from "react";

import { addDays, differenceInMilliseconds, getDate, startOfDay } from "date-fns";

import styles from "./Calendar.module.css";

const grabDate = () => getDate(new Date());

export function useDate() {
	const [date, setDate] = useState(grabDate());

	useEffect(() => {
		const now = new Date();
		const millisecondsUntilTomorrow = differenceInMilliseconds(startOfDay(addDays(now, 1)), now) + 1; // +1 for good measure

		let intervalRef: number;
		const dailyUpdateTicker = () => {
			intervalRef = window.setInterval(() => setDate(grabDate()), 24 * 60 * 60 * 1000); // 24hrs in milliseconds
		};

		const timeout = window.setTimeout(() => {
			setDate(grabDate());
			dailyUpdateTicker();
		}, millisecondsUntilTomorrow);

		return () => {
			window.clearTimeout(timeout);
			window.clearInterval(intervalRef);
		};
	}, []);

	return date;
}

export function CalendarOutline() {
	const date = useDate();

	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				// eslint-disable-next-line max-len
				d="M7.20001 1.80001C7.20001 1.13726 6.66275 0.600006 6.00001 0.600006C5.33727 0.600006 4.80001 1.13726 4.80001 1.80001V3.00001C4.80001 3.01155 4.80018 3.02305 4.8005 3.03452C4.09426 3.07761 3.59326 3.1745 3.16565 3.39238C2.48826 3.73753 1.93753 4.28826 1.59239 4.96564C1.20001 5.73572 1.20001 6.74382 1.20001 8.76001V16.44C1.20001 18.4562 1.20001 19.4643 1.59239 20.2344C1.93753 20.9118 2.48826 21.4625 3.16565 21.8076C3.93573 22.2 4.94382 22.2 6.96001 22.2H17.04C19.0562 22.2 20.0643 22.2 20.8344 21.8076C21.5118 21.4625 22.0625 20.9118 22.4076 20.2344C22.8 19.4643 22.8 18.4562 22.8 16.44V8.76001C22.8 6.74382 22.8 5.73572 22.4076 4.96564C22.0625 4.28826 21.5118 3.73753 20.8344 3.39238C20.4068 3.1745 19.9058 3.07761 19.1995 3.03452C19.1998 3.02305 19.2 3.01155 19.2 3.00001V1.80001C19.2 1.13726 18.6628 0.600006 18 0.600006C17.3373 0.600006 16.8 1.13726 16.8 1.80001V3.00001L13.2 3.00001V1.80001C13.2 1.13726 12.6628 0.600006 12 0.600006C11.3373 0.600006 10.8 1.13726 10.8 1.80001V3.00001L7.20001 3.00001V1.80001ZM2.76007 9.86424C2.76007 8.3857 2.76007 7.64643 3.04781 7.08171C3.30092 6.58496 3.70479 6.18109 4.20154 5.92798C4.76626 5.64024 5.50553 5.64024 6.98407 5.64024H17.0161C18.4946 5.64024 19.2339 5.64024 19.7986 5.92798C20.2954 6.18109 20.6992 6.58496 20.9523 7.08171C21.2401 7.64643 21.2401 8.3857 21.2401 9.86424V16.5362C21.2401 18.0148 21.2401 18.754 20.9523 19.3188C20.6992 19.8155 20.2954 20.2194 19.7986 20.4725C19.2339 20.7602 18.4946 20.7602 17.0161 20.7602H6.98407C5.50553 20.7602 4.76626 20.7602 4.20154 20.4725C3.70479 20.2194 3.30092 19.8155 3.04781 19.3188C2.76007 18.754 2.76007 18.0148 2.76007 16.5362V9.86424Z"
				fill="currentColor"
			/>
			<text
				x="50%"
				y="14"
				dominantBaseline="middle"
				textAnchor="middle"
				data-version="outline"
				className={styles.dateText}
			>
				{date}
			</text>
		</svg>
	);
}

export function CalendarFilled() {
	const date = useDate();

	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				// eslint-disable-next-line max-len
				d="M7.20001 1.80001C7.20001 1.13726 6.66275 0.600006 6.00001 0.600006C5.33727 0.600006 4.80001 1.13726 4.80001 1.80001V3.00001C4.80001 3.01155 4.80018 3.02305 4.8005 3.03452C4.09426 3.07761 3.59326 3.1745 3.16565 3.39238C2.48826 3.73753 1.93753 4.28826 1.59239 4.96564C1.20001 5.73572 1.20001 6.74382 1.20001 8.76001V16.44C1.20001 18.4562 1.20001 19.4643 1.59239 20.2344C1.93753 20.9118 2.48826 21.4625 3.16565 21.8076C3.93573 22.2 4.94382 22.2 6.96001 22.2H17.04C19.0562 22.2 20.0643 22.2 20.8344 21.8076C21.5118 21.4625 22.0625 20.9118 22.4076 20.2344C22.8 19.4643 22.8 18.4562 22.8 16.44V8.76001C22.8 6.74382 22.8 5.73572 22.4076 4.96564C22.0625 4.28826 21.5118 3.73753 20.8344 3.39238C20.4068 3.1745 19.9058 3.07761 19.1995 3.03452C19.1998 3.02305 19.2 3.01155 19.2 3.00001V1.80001C19.2 1.13726 18.6628 0.600006 18 0.600006C17.3373 0.600006 16.8 1.13726 16.8 1.80001V3.00001L13.2 3.00001V1.80001C13.2 1.13726 12.6628 0.600006 12 0.600006C11.3373 0.600006 10.8 1.13726 10.8 1.80001V3.00001L7.20001 3.00001V1.80001Z"
				fill="currentColor"
			/>
			<text
				x="50%"
				y="14"
				dominantBaseline="middle"
				textAnchor="middle"
				data-version="filled"
				className={styles.dateText}
			>
				{date}
			</text>
		</svg>
	);
}
